import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { FiTarget, FiTrendingUp, FiUsers, FiInfo, FiBarChart2, FiSun } from 'react-icons/fi'
import { 
  Container, 
  Card, 
  Grid, 
  Heading1, 
  Heading2, 
  BodyText, 
  SmallText, 
  Button, 
  StatusBadge,
  LoadingSpinner,
  Input 
} from '../components/ui'
import { theme, mediaQuery } from '../theme'
import { analyticsAPI } from '../services/api'

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.xl};
  
  ${mediaQuery.mobile} {
    flex-direction: column;
    gap: ${theme.spacing.md};
  }
`

const UserSelector = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`

const UserSelectionRow = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  
  ${mediaQuery.mobile} {
    flex-direction: column;
  }
`

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 2px solid ${theme.colors.primary.light};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.body.fontFamily};
  font-size: 16px;
  background: white;
  flex: 1;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 3px rgba(64, 92, 115, 0.1);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`

const DriverSelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
`

const DriverCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${props => props.checked ? theme.colors.primary.main : theme.colors.secondary.beige};
  color: ${props => props.checked ? 'white' : theme.colors.secondary.black};
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.checked ? theme.colors.primary.dark : theme.colors.primary.light}20;
  }
  
  input {
    margin: 0;
  }
`

const CorrelationMatrixContainer = styled.div`
  margin-bottom: ${theme.spacing.xl};
`

const MatrixTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: ${theme.colors.primary.main};
  margin-bottom: ${theme.spacing.sm};
`

const MatrixSubtitle = styled.p`
  color: ${theme.colors.primary.light};
  margin-bottom: ${theme.spacing.lg};
`

const MatrixGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing.lg};
`

const DriverCard = styled(Card)`
  background: linear-gradient(135deg, ${theme.colors.primary.main}10, ${theme.colors.secondary.green}10);
  border: 2px solid ${theme.colors.primary.main}30;
`

const DriverName = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: ${theme.colors.primary.main};
  margin-bottom: ${theme.spacing.md};
`

const CorrelationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`

const CorrelationRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`

const CorrelationRowBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: ${theme.spacing.xs};
`

const CorrelationRowDetails = styled.div`
  padding-left: 128px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const MetricName = styled.span`
  font-size: 14px;
  color: ${theme.colors.secondary.black};
  min-width: 120px;
`

const CorrelationBar = styled.div`
  flex: 1;
  height: 20px;
  background: ${theme.colors.primary.light}30;
  border-radius: ${theme.borderRadius.sm};
  position: relative;
  overflow: hidden;
`

const CorrelationFill = styled.div`
  height: 100%;
  width: ${props => props.width};
  background: ${props => props.color};
  border-radius: ${theme.borderRadius.sm};
  transition: all 0.3s ease;
`

const CorrelationValue = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.color};
  min-width: 60px;
  text-align: right;
`

const TimelineChartContainer = styled.div`
  margin-bottom: ${theme.spacing.xl};
`

const ChartTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${theme.colors.primary.main};
  margin-bottom: ${theme.spacing.md};
`

const ModeToggle = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
`

const ModeButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.sm};
  border: 2px solid ${props => props.active ? theme.colors.primary.main : theme.colors.primary.light + '50'};
  background: ${props => props.active ? theme.colors.primary.main : 'white'};
  color: ${props => props.active ? 'white' : theme.colors.secondary.black};
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary.main};
  }
`

const PopulationControls = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: flex-end;
  margin-bottom: ${theme.spacing.lg};
  flex-wrap: wrap;
`

const ArchetypeBanner = styled(Card)`
  background: linear-gradient(135deg, ${theme.colors.primary.main}15, ${theme.colors.secondary.green}15);
  border: 2px solid ${theme.colors.primary.main}35;
  margin-bottom: ${theme.spacing.xl};
`

const ArchetypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${theme.spacing.lg};
`

const ArchetypeLabel = styled(SmallText)`
  color: ${theme.colors.primary.light};
  display: block;
  margin-bottom: ${theme.spacing.xs};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
`

const ArchetypeValue = styled(BodyText)`
  font-size: 28px;
  font-weight: 700;
  color: ${theme.colors.primary.main};
  margin: 0;
`

const ArchetypeWorkSection = styled.div`
  margin-top: ${theme.spacing.lg};
  padding-top: ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.primary.main}25;
`

const ArchetypeWorkCard = styled.div`
  background: ${props => props.$highlight ? theme.colors.primary.main + '12' : 'white'};
  border: 1px solid ${props => props.$highlight ? theme.colors.primary.main + '50' : theme.colors.primary.light + '40'};
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
`

const ArchetypeWorkSteps = styled.ul`
  margin: ${theme.spacing.sm} 0 0;
  padding-left: ${theme.spacing.lg};
  color: ${theme.colors.secondary.black};
`

const ArchetypeWorkStep = styled.li`
  margin-bottom: ${theme.spacing.xs};
  font-size: 14px;
  color: ${theme.colors.primary.light};
`

const ArchetypeStackedBar = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  min-height: 88px;
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  border: 2px solid ${theme.colors.primary.main}35;
`

const ArchetypeStackSegment = styled.div`
  flex: ${props => props.$flex} 1 0;
  min-width: 72px;
  background: ${props => props.$color};
  color: ${props => props.$textColor || 'white'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.md} ${theme.spacing.sm};
  text-align: center;
`

const ArchetypeStackLabel = styled.div`
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
`

const ArchetypeStackPercent = styled.div`
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
  margin-top: ${theme.spacing.xs};
`

const ArchetypeStackCount = styled.div`
  font-size: 12px;
  opacity: 0.9;
  margin-top: ${theme.spacing.xs};
`

const ARCHETYPE_COLORS = {
  Pacer: theme.colors.chart.primary,
  Burner: theme.colors.status.warning,
  Lifter: theme.colors.chart.secondary,
  Believer: theme.colors.chart.quaternary
}

const ARCHETYPE_PLURAL_LABELS = {
  Pacer: 'Pacers',
  Burner: 'Burners',
  Lifter: 'Lifters',
  Believer: 'Believers'
}

const ARCHETYPE_TEXT_COLORS = {
  Pacer: 'white',
  Burner: theme.colors.secondary.black,
  Lifter: 'white',
  Believer: 'white'
}

const getArchetypePluralLabel = (archetype) => ARCHETYPE_PLURAL_LABELS[archetype] || archetype

const PredictorsPage = () => {
  const [analysisMode, setAnalysisMode] = useState('individual')
  const [userSelectionMode, setUserSelectionMode] = useState('id')
  const [selectedUserId, setSelectedUserId] = useState('')
  const [activeUsers, setActiveUsers] = useState([])
  const [isLoadingUserList, setIsLoadingUserList] = useState(false)
  const [userListError, setUserListError] = useState(null)
  const [minimumEntries, setMinimumEntries] = useState(18)
  const [predictorData, setPredictorData] = useState(null)
  const [selectedDrivers, setSelectedDrivers] = useState(['pressure', 'zone', 'mentalReserves', 'emotional'])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showPopulationStats, setShowPopulationStats] = useState(false)
  const [showArchetypeWork, setShowArchetypeWork] = useState(false)
  
  const availableDrivers = [
    { key: 'pressure', label: 'Pressure', icon: FiTrendingUp },
    { key: 'zone', label: 'Pacing', icon: FiTarget },
    { key: 'mentalReserves', label: 'Mindstate Strength', icon: FiSun },
    { key: 'emotional', label: 'Emotional Factors', icon: FiUsers }
  ]

  useEffect(() => {
    setSelectedDrivers((prev) => {
      const valid = prev.filter((key) => availableDrivers.some((driver) => driver.key === key))
      return valid.length > 0 ? valid : availableDrivers.map((driver) => driver.key)
    })
  }, [])

  useEffect(() => {
    if (analysisMode !== 'individual' || userSelectionMode !== 'dropdown') return

    let cancelled = false

    const loadActiveUsers = async () => {
      setIsLoadingUserList(true)
      setUserListError(null)

      try {
        const response = await analyticsAPI.getActiveUsers()
        if (cancelled) return

        if (response.data.success) {
          setActiveUsers(response.data.data || [])
        } else {
          throw new Error(response.data.error || 'Failed to load user list')
        }
      } catch (err) {
        if (cancelled) return
        const message = err.response?.data?.error || err.message || 'Failed to load user list'
        setUserListError(message)
        setActiveUsers([])
      } finally {
        if (!cancelled) setIsLoadingUserList(false)
      }
    }

    loadActiveUsers()

    return () => {
      cancelled = true
    }
  }, [analysisMode, userSelectionMode])

  const visibleDrivers = useMemo(
    () => selectedDrivers.filter((key) => availableDrivers.some((driver) => driver.key === key)),
    [selectedDrivers]
  )

  const loadPredictorData = async (userId) => {
    setIsLoading(true)
    setError(null)
    setShowArchetypeWork(false)
    
    try {
      console.log(`🔍 Loading predictor data for user: ${userId}`)
      const response = await analyticsAPI.getResilienceDrivers(userId)
      
      if (response.data.success) {
        setPredictorData(response.data.data)
        console.log('✅ Predictor data loaded:', response.data.data)
      } else {
        throw new Error(response.data.error || 'Failed to load predictor data')
      }
    } catch (err) {
      console.error('❌ Error loading predictor data:', err)
      const message = err.response?.data?.error || err.message || 'Failed to load predictor data'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const loadPopulationAnalysis = async () => {
    setIsLoading(true)
    setError(null)
    setPredictorData(null)

    try {
      console.log(`🔍 Loading population predictor analysis (min ${minimumEntries} entries)`)
      const response = await analyticsAPI.getPopulationResilienceDrivers(minimumEntries || 18)

      if (response.data.success) {
        setPredictorData(response.data.data)
        console.log('✅ Population predictor data loaded:', response.data.data)
      } else {
        throw new Error(response.data.error || 'Failed to load population predictor data')
      }
    } catch (err) {
      console.error('❌ Error loading population predictor data:', err)
      const message = err.response?.data?.error || err.message || 'Failed to load population predictor data'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleModeChange = (mode) => {
    setAnalysisMode(mode)
    setPredictorData(null)
    setError(null)
    if (mode !== 'population') {
      setShowPopulationStats(false)
    } else {
      setShowArchetypeWork(false)
    }
  }

  const handleUserSelectionModeChange = (mode) => {
    setUserSelectionMode(mode)
    setSelectedUserId('')
    setPredictorData(null)
    setError(null)
  }

  const formatUserOptionLabel = (user) => {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
    const label = name || user.displayName || user.userId
    const entryCount = user.entryCount ?? 0
    return `${label} (${entryCount.toLocaleString()} ${entryCount === 1 ? 'entry' : 'entries'})`
  }

  const handleAnalyze = () => {
    if (analysisMode === 'population') {
      loadPopulationAnalysis()
    } else {
      loadPredictorData(selectedUserId)
    }
  }

  const isPopulationMode = analysisMode === 'population'

  const formatEntryDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return 'N/A'
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const timelineWindows = predictorData?.timeline?.windows || []
  const firstEntryDate = timelineWindows[0]?.dateRange?.startDate
  const mostRecentEntryDate = timelineWindows[timelineWindows.length - 1]?.dateRange?.endDate

  const handleDriverToggle = (driverKey) => {
    setSelectedDrivers(prev => 
      prev.includes(driverKey) 
        ? prev.filter(d => d !== driverKey)
        : [...prev, driverKey]
    )
  }

  const getDriverColor = (driver) => {
    const colors = {
      pressure: theme.colors.status.error,
      zone: theme.colors.status.warning,
      mentalReserves: theme.colors.secondary.green,
      emotional: theme.colors.primary.main
    }
    return colors[driver] || theme.colors.primary.main
  }

  const flattenCorrelationDetails = (details) => {
    if (!details || typeof details !== 'object') return null

    const result = {}
    for (const [key, value] of Object.entries(details)) {
      if (value && typeof value.pooledR === 'number' && !Number.isNaN(value.pooledR)) {
        result[key] = value.pooledR
      } else if (value && typeof value.mean === 'number' && !Number.isNaN(value.mean)) {
        result[key] = value.mean
      } else if (typeof value === 'object' && value !== null) {
        const nested = flattenCorrelationDetails(value)
        if (nested && Object.keys(nested).length > 0) {
          result[key] = nested
        }
      }
    }
    return Object.keys(result).length > 0 ? result : null
  }

  const getCorrelationSource = () => {
    const source = predictorData?.correlations || predictorData?.data?.correlations
    if (!source || source.error) return null
    return source
  }

  const getDriverCorrelations = (driverKey) => {
    const correlations = getCorrelationSource()
    const details = predictorData?.correlationDetails

    if (!correlations) return null

    if (driverKey === 'mentalReserves') {
      const levels = correlations.mentalReserves?.levels
        || correlations.zone?.mentalReservesPredictive
        || flattenCorrelationDetails(details?.mentalReserves?.levels)
        || flattenCorrelationDetails(details?.zone?.mentalReservesPredictive)

      return levels ? { levels } : null
    }

    const direct = correlations[driverKey]
    const fromDetails = flattenCorrelationDetails(details?.[driverKey])

    if (driverKey === 'emotional') {
      const merged = { ...(fromDetails || {}), ...(direct || {}) }
      const normalized = {}

      for (const [factor, value] of Object.entries(merged)) {
        const level = typeof value === 'number' ? value : value?.level
        if (level === null || level === undefined || Number.isNaN(level)) continue
        normalized[factor] = { level }
      }

      return Object.keys(normalized).length > 0 ? normalized : null
    }

    if (direct && Object.keys(direct).length > 0 && Object.values(direct).some(hasValidCorrelationValue)) {
      return direct
    }

    return fromDetails
  }

  const formatCorrelationValue = (value) => {
    if (value === null || value === undefined) return 'N/A'
    return (value * 100).toFixed(2)
  }

  const getCorrelationColor = (value) => {
    if (value === null || value === undefined) return theme.colors.primary.light
    return value > 0 ? theme.colors.status.success : value < 0 ? theme.colors.status.error : theme.colors.primary.light
  }

  const getCorrelationStrength = (value) => {
    if (value === null || value === undefined) return 0
    return Math.abs(value) * 100
  }

  const hasValidCorrelationValue = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return false
    if (typeof value === 'number') return true
    if (typeof value === 'object') {
      if (value.trend !== null && value.trend !== undefined && !Number.isNaN(value.trend)) return true
      if (value.level !== null && value.level !== undefined && !Number.isNaN(value.level)) return true
      return Object.values(value).some(hasValidCorrelationValue)
    }
    return false
  }

  const getCorrelationStat = (...path) => {
    if (!isPopulationMode || !predictorData?.correlationDetails) return null

    let node = predictorData.correlationDetails
    for (const key of path) {
      if (!node || typeof node !== 'object') return null
      node = node[key]
    }

    if (node && (typeof node.pooledR === 'number' || typeof node.mean === 'number')) {
      return node
    }

    return null
  }

  const getPopulationConfidence = (stats) => {
    if (!stats) return null
    const r = stats.pooledR ?? stats.mean
    if (r === null || r === undefined || Number.isNaN(r)) return null
    if (stats.heterogeneity?.level === 'high') return 'moderate'
    if (Math.abs(r) > 0.3) return 'high'
    return 'moderate'
  }

  const renderPopulationRowDetails = (stats) => {
    if (!stats) return null

    const het = stats.heterogeneity
    const confidence = getPopulationConfidence(stats)

    return (
      <CorrelationRowDetails>
        {stats.pooledCI && (
          <SmallText style={{ color: theme.colors.primary.light }}>
            95% CI: {formatCorrelationValue(stats.pooledCI[0])} to {formatCorrelationValue(stats.pooledCI[1])}
          </SmallText>
        )}
        {het?.level && (
          <SmallText style={{ color: theme.colors.primary.light }}>
            Heterogeneity: {het.level} (I²={het.I2}%, {het.pctPositive}% positive, {het.pctNegative}% negative)
          </SmallText>
        )}
        {confidence && (
          <SmallText style={{ color: theme.colors.primary.light }}>
            Confidence: {confidence}
          </SmallText>
        )}
      </CorrelationRowDetails>
    )
  }

  const renderCorrelationRow = ({ rowKey, label, value, stats }) => {
    if (value === null || value === undefined || Number.isNaN(value)) return null

    const color = getCorrelationColor(value)
    const strength = getCorrelationStrength(value)

    return (
      <CorrelationRowBlock key={rowKey}>
        <CorrelationRow>
          <MetricName>{label}</MetricName>
          <CorrelationBar>
            <CorrelationFill width={`${strength}%`} color={color} />
          </CorrelationBar>
          <CorrelationValue color={color}>
            {formatCorrelationValue(value)}
          </CorrelationValue>
        </CorrelationRow>
        {isPopulationMode && showPopulationStats && renderPopulationRowDetails(stats)}
      </CorrelationRowBlock>
    )
  }

  const renderScalarCorrelationRows = (metrics, data, keyPrefix = '', statPathPrefix = []) => {
    return metrics.flatMap(({ key, label }) => {
      const correlation = data?.[key]
      const row = renderCorrelationRow({
        rowKey: `${keyPrefix}${key}`,
        label,
        value: correlation,
        stats: getCorrelationStat(...statPathPrefix, key)
      })
      return row ? [row] : []
    })
  }

  // Helper function to get public names for emotional factors
  const getEmotionalFactorName = (metric) => {
    const factorNames = {
      'hours': 'Personal Identity',
      'focus': 'Focus',
      'calm': 'Calm',
      'happiness': 'Positivity',
      'depression': 'Satisfaction',
      'mania': 'Control',
      'energy': 'Energy'
    }
    return factorNames[metric] || metric
  }

  return (
    <Container>
      <PageHeader>
        <div>
          <Heading1>Resilience Predictors</Heading1>
          <BodyText>
            {isPopulationMode
              ? 'Analyze how resilience driver correlations trend across all users with sufficient data'
              : 'Analyze how changes in factors correlate with changes in Mental Resilience over time'}
          </BodyText>
        </div>
        
        <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap' }}>
          <Button 
            onClick={handleAnalyze}
            disabled={isLoading || (analysisMode === 'individual' && !selectedUserId.trim())}
          >
            {isLoading ? <LoadingSpinner style={{ width: '16px', height: '16px', margin: '0' }} /> : 'Analyze'}
          </Button>
        </div>
      </PageHeader>

      {/* Analysis Mode Toggle */}
      <ModeToggle>
        <ModeButton active={analysisMode === 'individual'} onClick={() => handleModeChange('individual')}>
          <FiUsers style={{ marginRight: theme.spacing.xs, verticalAlign: 'middle' }} />
          Individual User
        </ModeButton>
        <ModeButton active={analysisMode === 'population'} onClick={() => handleModeChange('population')}>
          <FiBarChart2 style={{ marginRight: theme.spacing.xs, verticalAlign: 'middle' }} />
          Population Analysis
        </ModeButton>
      </ModeToggle>

      {/* User Selector (individual mode) */}
      {analysisMode === 'individual' && (
      <UserSelector>
        <ModeToggle>
          <ModeButton
            active={userSelectionMode === 'id'}
            onClick={() => handleUserSelectionModeChange('id')}
          >
            User ID
          </ModeButton>
          <ModeButton
            active={userSelectionMode === 'dropdown'}
            onClick={() => handleUserSelectionModeChange('dropdown')}
          >
            Select User
          </ModeButton>
        </ModeToggle>

        {userSelectionMode === 'id' ? (
          <UserSelectionRow>
            <Input
              placeholder="Enter User ID"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              style={{ flex: 1 }}
            />
          </UserSelectionRow>
        ) : (
          <UserSelectionRow>
            <Select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              disabled={isLoadingUserList}
            >
              <option value="">
                {isLoadingUserList ? 'Loading users...' : 'Select a user'}
              </option>
              {activeUsers.map((user) => (
                <option key={user.userId} value={user.userId}>
                  {formatUserOptionLabel(user)}
                </option>
              ))}
            </Select>
          </UserSelectionRow>
        )}

        {userListError && userSelectionMode === 'dropdown' && (
          <SmallText style={{ color: theme.colors.status.error, margin: 0 }}>
            {userListError}
          </SmallText>
        )}
      </UserSelector>
      )}

      {/* Population Controls */}
      {analysisMode === 'population' && (
        <PopulationControls>
          <div>
            <SmallText style={{ color: theme.colors.primary.light, marginBottom: theme.spacing.xs, display: 'block' }}>
              Minimum Check-Ins
            </SmallText>
            <Input
              type="number"
              value={minimumEntries}
              onChange={(e) => {
                const value = e.target.value
                if (value === '') {
                  setMinimumEntries('')
                  return
                }
                const parsed = parseInt(value, 10)
                if (!Number.isNaN(parsed)) setMinimumEntries(parsed)
              }}
              style={{ width: '120px' }}
            />
          </div>
          <BodyText style={{ margin: 0, color: theme.colors.primary.light }}>
            18 check-ins minimum required
          </BodyText>
          <ModeButton
            type="button"
            active={showPopulationStats}
            onClick={() => setShowPopulationStats(prev => !prev)}
          >
            {showPopulationStats ? 'Hide Statistical Details' : 'Show Statistical Details'}
          </ModeButton>
        </PopulationControls>
      )}

      {/* Driver Selector */}
      <DriverSelector>
        {availableDrivers.map(driver => (
          <DriverCheckbox 
            key={driver.key}
            checked={selectedDrivers.includes(driver.key)}
          >
            <input
              type="checkbox"
              checked={selectedDrivers.includes(driver.key)}
              onChange={() => handleDriverToggle(driver.key)}
            />
            <driver.icon />
            {driver.label}
          </DriverCheckbox>
        ))}
      </DriverSelector>

      {/* Error Display */}
      {error && (
        <Card style={{ 
          background: theme.colors.status.error + '20', 
          border: `2px solid ${theme.colors.status.error}50`,
          marginBottom: theme.spacing.lg
        }}>
          <BodyText style={{ color: theme.colors.status.error, margin: 0, whiteSpace: 'pre-wrap' }}>
            {error}
          </BodyText>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <Card style={{ textAlign: 'center', padding: theme.spacing.xl }}>
          <LoadingSpinner />
          <BodyText style={{ marginTop: theme.spacing.md }}>
            {isPopulationMode ? 'Analyzing population resilience drivers...' : 'Analyzing resilience drivers...'}
          </BodyText>
        </Card>
      )}

      {/* Main Analysis Content */}
      {predictorData && !isLoading && (
        <>
          {!isPopulationMode && predictorData.archetypes && (
            <ArchetypeBanner>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: theme.spacing.md, flexWrap: 'wrap', marginBottom: theme.spacing.md }}>
                <Heading2 style={{ margin: 0 }}>Your Archetypes</Heading2>
                {predictorData.archetypes.work?.length > 0 && (
                  <ModeButton
                    type="button"
                    active={showArchetypeWork}
                    onClick={() => setShowArchetypeWork(prev => !prev)}
                  >
                    {showArchetypeWork ? 'Hide Calculation Details' : 'Show Calculation Details'}
                  </ModeButton>
                )}
              </div>
              {predictorData.archetypes.primary && (
                <ArchetypeGrid>
                  <div>
                    <ArchetypeLabel>Primary</ArchetypeLabel>
                    <ArchetypeValue>{predictorData.archetypes.primary}</ArchetypeValue>
                  </div>
                  {predictorData.archetypes.secondary && (
                    <div>
                      <ArchetypeLabel>Secondary</ArchetypeLabel>
                      <ArchetypeValue>{predictorData.archetypes.secondary}</ArchetypeValue>
                    </div>
                  )}
                </ArchetypeGrid>
              )}

              {showArchetypeWork && predictorData.archetypes.work?.length > 0 && (
                <ArchetypeWorkSection>
                  <Heading2 style={{ marginBottom: theme.spacing.sm, fontSize: '20px' }}>How We Calculated This</Heading2>
                  <BodyText style={{ marginBottom: theme.spacing.md, color: theme.colors.primary.light }}>
                    Each archetype gets a score. Primary and secondary are the two highest scores above zero.
                    Burners are Pacers whose Over Pacing correlation leads Optimal and Under; they are labeled Burner instead of Pacer.
                  </BodyText>
                  {predictorData.archetypes.work.map((item) => {
                    const isPrimary = item.archetype === predictorData.archetypes.primary
                    const isSecondary = item.archetype === predictorData.archetypes.secondary

                    return (
                      <ArchetypeWorkCard key={item.archetype} $highlight={isPrimary || isSecondary}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: theme.spacing.md, flexWrap: 'wrap' }}>
                          <div>
                            <BodyText style={{ fontWeight: 700, margin: 0 }}>
                              {item.archetype}
                              {isPrimary && (
                                <SmallText style={{ marginLeft: theme.spacing.sm, color: theme.colors.primary.main }}>
                                  Primary
                                </SmallText>
                              )}
                              {isSecondary && (
                                <SmallText style={{ marginLeft: theme.spacing.sm, color: theme.colors.primary.light }}>
                                  Secondary
                                </SmallText>
                              )}
                            </BodyText>
                            <SmallText style={{ color: theme.colors.primary.light, display: 'block', marginTop: theme.spacing.xs }}>
                              {item.summary}
                            </SmallText>
                          </div>
                          <BodyText style={{ fontWeight: 600, margin: 0 }}>
                            Score: {formatCorrelationValue(item.score)}
                          </BodyText>
                        </div>
                        {item.steps?.length > 0 && (
                          <ArchetypeWorkSteps>
                            {item.steps.map((step) => (
                              <ArchetypeWorkStep key={step}>{step}</ArchetypeWorkStep>
                            ))}
                          </ArchetypeWorkSteps>
                        )}
                      </ArchetypeWorkCard>
                    )
                  })}
                </ArchetypeWorkSection>
              )}
            </ArchetypeBanner>
          )}

          {isPopulationMode && predictorData.archetypeDistribution?.archetypes?.length > 0 && (
            <ArchetypeBanner>
              <Heading2 style={{ marginBottom: theme.spacing.sm }}>Population Archetypes</Heading2>
              <BodyText style={{ marginBottom: theme.spacing.lg, color: theme.colors.primary.light }}>
                {predictorData.archetypeDistribution.totalAssignments?.toLocaleString() || 0} archetypes identified across{' '}
                {predictorData.archetypeDistribution.analyzedUsers?.toLocaleString() || 0} analyzed users.
                Percentages are share of all primary and secondary archetype assignments.
                Burners are counted separately from Pacers (Burner replaces Pacer when Over Pacing leads).
              </BodyText>
              <ArchetypeStackedBar>
                {predictorData.archetypeDistribution.archetypes
                  .filter((item) => item.count > 0)
                  .map((item) => (
                    <ArchetypeStackSegment
                      key={item.archetype}
                      $flex={item.percent}
                      $color={ARCHETYPE_COLORS[item.archetype]}
                      $textColor={ARCHETYPE_TEXT_COLORS[item.archetype]}
                    >
                      <ArchetypeStackLabel>{getArchetypePluralLabel(item.archetype)}</ArchetypeStackLabel>
                      <ArchetypeStackPercent>{item.percent.toFixed(1)}%</ArchetypeStackPercent>
                      <ArchetypeStackCount>
                        {item.count.toLocaleString()} ({item.primaryCount} primary · {item.secondaryCount} secondary)
                      </ArchetypeStackCount>
                    </ArchetypeStackSegment>
                  ))}
              </ArchetypeStackedBar>
              {predictorData.archetypeDistribution.archetypes.some((item) => item.count === 0) && (
                <SmallText style={{ display: 'block', marginTop: theme.spacing.md, color: theme.colors.primary.light }}>
                  No assignments:{' '}
                  {predictorData.archetypeDistribution.archetypes
                    .filter((item) => item.count === 0)
                    .map((item) => getArchetypePluralLabel(item.archetype))
                    .join(', ')}
                </SmallText>
              )}
              {predictorData.archetypeDistribution.unassigned > 0 && (
                <SmallText style={{ display: 'block', marginTop: theme.spacing.md, color: theme.colors.primary.light }}>
                  {predictorData.archetypeDistribution.unassigned.toLocaleString()} analyzed users had no qualifying primary or secondary archetype.
                </SmallText>
              )}
            </ArchetypeBanner>
          )}

          {/* Correlation Matrix */}
          <CorrelationMatrixContainer>
            <MatrixTitle>Driver Correlation Matrix</MatrixTitle>
            <MatrixSubtitle>
              {isPopulationMode
                ? `Average correlation strength across ${predictorData.summary?.analyzedUsers || 0} users with valid rolling-window data (${predictorData.summary?.qualifiedUsers || 0} qualified by unique days, minimum ${predictorData.minimumEntries || minimumEntries})`
                : 'How prior-period drivers predict resilience in the next period (positive = higher driver tends to precede higher resilience)'}
            </MatrixSubtitle>
            
            <MatrixGrid>
              {!getCorrelationSource() && (
                <Card style={{ gridColumn: '1 / -1' }}>
                  <BodyText style={{ margin: 0, color: theme.colors.primary.light }}>
                    Correlation data is not available for this analysis.
                  </BodyText>
                </Card>
              )}

              {getCorrelationSource() && visibleDrivers.length === 0 && (
                <Card style={{ gridColumn: '1 / -1' }}>
                  <BodyText style={{ margin: 0, color: theme.colors.primary.light }}>
                    Select at least one driver above to view correlation results.
                  </BodyText>
                </Card>
              )}

              {getCorrelationSource() && visibleDrivers.map(driverKey => {
                const driver = availableDrivers.find(d => d.key === driverKey)
                if (!driver) return null

                const correlations = getDriverCorrelations(driverKey)

                const hasValidCorrelations = correlations && (
                  driverKey === 'mentalReserves'
                    ? !!(correlations.levels && Object.values(correlations.levels).some(hasValidCorrelationValue))
                    : Object.values(correlations).some(hasValidCorrelationValue)
                )
                
                return (
                  <DriverCard key={driverKey}>
                    <DriverName>
                      <driver.icon style={{ marginRight: theme.spacing.sm }} />
                      {driver.label}
                    </DriverName>
                    <CorrelationList>
                      {!correlations ? (
                        <BodyText style={{ margin: 0, color: theme.colors.primary.light, fontStyle: 'italic' }}>
                          No correlation data available for this driver.
                        </BodyText>
                      ) : (() => {
                        // Collect correlation data - handle zone specially since it's an object
                        const correlationData = []
                        
                        if (driverKey === 'zone' && typeof correlations === 'object') {
                          correlationData.push({
                            metric: 'zone',
                            correlation: correlations,
                            type: 'zone'
                          })
                        }
                        
                        // Handle other correlations (skip if this is the zone driver)
                        const otherCorrelations = driverKey === 'zone' ? [] : Object.entries(correlations)
                        const otherCorrelationData = otherCorrelations.map(([metric, correlation]) => {
                          if (correlation === null || correlation === undefined) {
                            return null
                          }
                          
                          if (metric === 'total' || metric === 'trend') {
                            return null
                          }
                          
                          // Handle pressure levels (0-10)
                          if (typeof correlation === 'object' && correlation !== null && metric === 'levels') {
                            return {
                              metric,
                              correlation,
                              type: 'pressureLevels'
                            }
                          }
                          
                          // Handle emotional factors with both trend and level
                          if (typeof correlation === 'object' && correlation !== null && metric !== 'levels') {
                            return {
                              metric,
                              correlation,
                              type: 'emotional'
                            }
                          }
                          
                          // Handle simple numeric correlations
                          if (typeof correlation === 'number') {
                            return {
                              metric,
                              correlation,
                              type: 'simple'
                            }
                          }

                          return null
                        }).filter(Boolean)
                        
                        // Combine zone and other correlations
                        correlationData.push(...otherCorrelationData)
                        
                        // Separate different types of correlations
                        const pressureLevelFactors = correlationData.filter(item => item.type === 'pressureLevels')
                        const zoneFactors = correlationData.filter(item => item.type === 'zone')
                        const simpleCorrelations = correlationData.filter(item => item.type === 'simple')
                        
                        // Create pressure level correlation rows
                        const pressureLevelRows = driverKey === 'pressure'
                          ? pressureLevelFactors.flatMap(({ correlation }) => {
                              const validLevels = Object.entries(correlation)
                                .filter(([level, value]) => value !== null && value !== undefined && !Number.isNaN(value))
                                .sort(([a], [b]) => parseInt(b) - parseInt(a))
                              
                              return validLevels.flatMap(([level, value]) => {
                                const row = renderCorrelationRow({
                                  rowKey: `pressure-level-${level}`,
                                  label: `Pressure Level ${level}`,
                                  value,
                                  stats: getCorrelationStat('pressure', 'levels', level)
                                })
                                return row ? [row] : []
                              })
                            })
                          : []
                        
                        // Create pacing correlation rows
                        const zoneRows = []
                        if (driverKey === 'zone') {
                          const zoneData = zoneFactors.length > 0 ? zoneFactors[0].correlation : correlations
                          const zoneMetrics = [
                            { key: 'overPacingPredictive', label: 'Over Pacing Days' },
                            { key: 'optimalTimePredictive', label: 'Optimal Pace Days' },
                            { key: 'underPacingPredictive', label: 'Under Pacing Days' }
                          ]

                          zoneRows.push(...renderScalarCorrelationRows(zoneMetrics, zoneData, 'zone-', ['zone']))
                        }
                        
                        // Create mental reserves level correlation rows
                        const mentalReservesRows = []
                        if (driverKey === 'mentalReserves' && correlations.levels) {
                          Object.entries(correlations.levels)
                            .filter(([, value]) => value !== null && value !== undefined && !Number.isNaN(value))
                            .sort(([a], [b]) => parseInt(b) - parseInt(a))
                            .forEach(([level, value]) => {
                              const row = renderCorrelationRow({
                                rowKey: `mental-reserves-${level}`,
                                label: `Mindstate Strength ${level}`,
                                value,
                                stats: getCorrelationStat('zone', 'mentalReservesPredictive', level)
                              })
                              if (row) mentalReservesRows.push(row)
                            })
                        }

                        // Create emotional factor correlation rows
                        const emotionalRows = driverKey === 'emotional'
                          ? Object.entries(correlations).flatMap(([metric, correlation]) => {
                              const level = typeof correlation === 'number' ? correlation : correlation?.level
                              if (level === null || level === undefined || Number.isNaN(level)) {
                                return []
                              }

                              const row = renderCorrelationRow({
                                rowKey: `emotional-${metric}`,
                                label: getEmotionalFactorName(metric),
                                value: level,
                                stats: getCorrelationStat('emotional', metric, 'level')
                              })
                              return row ? [row] : []
                            })
                          : []
                        
                        // Create simple correlation rows (skip drivers with dedicated renderers)
                        const dedicatedDrivers = ['pressure', 'zone', 'mentalReserves', 'emotional']
                        const simpleRows = dedicatedDrivers.includes(driverKey)
                          ? []
                          : simpleCorrelations.flatMap(({ metric, correlation }) => {
                          const row = renderCorrelationRow({
                            rowKey: metric,
                            label: metric,
                            value: correlation,
                            stats: null
                          })
                          return row ? [row] : []
                        })
                        
                        const correlationRows = [...pressureLevelRows, ...zoneRows, ...mentalReservesRows, ...emotionalRows, ...simpleRows]

                        if (correlationRows.length > 0) {
                          return correlationRows
                        }

                        return (
            <div style={{ 
              padding: theme.spacing.md, 
              textAlign: 'center',
              color: theme.colors.primary.light,
              fontStyle: 'italic'
            }}>
              Correlation analysis requires more data points. 
              <br />
              Showing driver metrics instead.
            </div>
                        )
                      })()}
                    </CorrelationList>
                    
                    {/* Show driver metrics when correlations are not available (individual mode only) */}
                    {!hasValidCorrelations && !isPopulationMode && predictorData.driverMetrics && (
                      <div style={{ marginTop: theme.spacing.md, padding: theme.spacing.md, borderTop: `1px solid ${theme.colors.primary.light}30` }}>
                        <SmallText style={{ color: theme.colors.primary.light, marginBottom: theme.spacing.sm }}>
                          Recent Driver Metrics (Latest Window):
                        </SmallText>
                        {(() => {
                          const latestMetrics = predictorData.driverMetrics[predictorData.driverMetrics.length - 1]
                          if (!latestMetrics) return <div>No metrics available</div>
                          
                          switch (driverKey) {
                            case 'pressure':
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                                  <SmallText>Average Pressure: {latestMetrics.pressure?.averagePressure || 'N/A'}</SmallText>
                                  <SmallText>Volatility: {latestMetrics.pressure?.pressureVolatility || 'N/A'}</SmallText>
                                  <SmallText>High Pressure %: {latestMetrics.pressure?.highPressurePercentage || 'N/A'}</SmallText>
                                </div>
                              )
                            case 'zone':
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                                  <SmallText>Optimal Pace %: {latestMetrics.zone?.optimalZonePercentage || 'N/A'}</SmallText>
                                  <SmallText>Over Pacing %: {latestMetrics.zone?.overPacingPercentage || 'N/A'}</SmallText>
                                  <SmallText>Under Pacing %: {latestMetrics.zone?.underPacingPercentage || 'N/A'}</SmallText>
                                </div>
                              )
                            case 'mentalReserves':
                              return (
                                <SmallText style={{ color: theme.colors.primary.light }}>
                                  Mindstate strength levels are calculated from emotional factor scores in each window.
                                </SmallText>
                              )
                            case 'emotional':
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                                  {Object.entries(latestMetrics.emotional?.factors || {}).slice(0, 3).map(([factor, data]) => (
                                    <SmallText key={factor}>
                                      {factor}: {data?.averageLevel || 'N/A'}
                                    </SmallText>
                                  ))}
                                </div>
                              )
                            default:
                              return <div>No metrics available</div>
                          }
                        })()}
                      </div>
                    )}
                  </DriverCard>
                )
              })}
            </MatrixGrid>
          </CorrelationMatrixContainer>

          {/* Data Summary */}
          <Card style={{ background: theme.colors.primary.main + '10' }}>
            <Heading2 style={{ marginBottom: theme.spacing.md }}>
              {isPopulationMode ? 'Population Analysis Summary' : 'Analysis Summary'}
            </Heading2>
            <Grid columns="repeat(auto-fit, minmax(200px, 1fr))">
              {isPopulationMode ? (
                <>
                  <div>
                    <SmallText style={{ color: theme.colors.primary.light }}>Users Analyzed</SmallText>
                    <BodyText style={{ fontWeight: 600 }}>{predictorData.summary?.analyzedUsers || 0}</BodyText>
                  </div>
                  <div>
                    <SmallText style={{ color: theme.colors.primary.light }}>Qualified Users</SmallText>
                    <BodyText style={{ fontWeight: 600 }}>
                      {predictorData.summary?.qualifiedUsers || 0} of {predictorData.summary?.totalUsers || 0}
                    </BodyText>
                  </div>
                  <div>
                    <SmallText style={{ color: theme.colors.primary.light }}>Minimum Check-Ins</SmallText>
                    <BodyText style={{ fontWeight: 600 }}>{predictorData.minimumEntries || minimumEntries}</BodyText>
                  </div>
                  <div>
                    <SmallText style={{ color: theme.colors.primary.light }}>Avg Windows Per User</SmallText>
                    <BodyText style={{ fontWeight: 600 }}>{predictorData.summary?.avgWindowsPerUser || 0}</BodyText>
                  </div>
                  <div>
                    <SmallText style={{ color: theme.colors.primary.light }}>Processing Time</SmallText>
                    <BodyText style={{ fontWeight: 600 }}>
                      {predictorData.summary?.processingTimeMs
                        ? `${(predictorData.summary.processingTimeMs / 1000).toFixed(1)}s`
                        : 'N/A'}
                    </BodyText>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <SmallText style={{ color: theme.colors.primary.light }}>Windows Analyzed</SmallText>
                    <BodyText style={{ fontWeight: 600 }}>{predictorData.driverMetrics?.length || 0}</BodyText>
                  </div>
                  <div>
                    <SmallText style={{ color: theme.colors.primary.light }}>Total Entries</SmallText>
                    <BodyText style={{ fontWeight: 600 }}>{predictorData.timeline?.totalEntries || 0}</BodyText>
                  </div>
                  <div>
                    <SmallText style={{ color: theme.colors.primary.light }}>First Entry</SmallText>
                    <BodyText style={{ fontWeight: 600 }}>{formatEntryDate(firstEntryDate)}</BodyText>
                  </div>
                  <div>
                    <SmallText style={{ color: theme.colors.primary.light }}>Most Recent Entry</SmallText>
                    <BodyText style={{ fontWeight: 600 }}>{formatEntryDate(mostRecentEntryDate)}</BodyText>
                  </div>
                </>
              )}
            </Grid>
          </Card>
        </>
      )}

      {/* Information Card */}
      <Card style={{ 
        background: 'linear-gradient(135deg, ' + theme.colors.primary.light + '20, ' + theme.colors.primary.main + '10)',
        borderLeft: '4px solid ' + theme.colors.primary.main
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: theme.spacing.md }}>
          <FiInfo style={{ fontSize: '24px', color: theme.colors.primary.main, marginTop: '4px' }} />
          <div>
            <Heading2 style={{ marginBottom: theme.spacing.sm }}>About Resilience Predictors</Heading2>
            <BodyText style={{ marginBottom: theme.spacing.sm }}>
              • <strong>Population Analysis:</strong> Runs the same per-user correlation analysis across all qualifying users, then combines results with random-effects meta-analysis (weighted by window count) — revealing which predictors tend to matter most broadly
            </BodyText>
            <BodyText style={{ marginBottom: theme.spacing.sm }}>
              • <strong>All predictors:</strong> Use the same look-ahead model — how a driver in the prior rolling window relates to resilience score in the next window
            </BodyText>
            <BodyText style={{ marginBottom: theme.spacing.sm }}>
              • <strong>Pressure:</strong> Time spent at each pressure level (0–10) in the prior window
            </BodyText>
            <BodyText style={{ marginBottom: theme.spacing.sm }}>
              • <strong>Pacing:</strong> Over, optimal, and under pacing percentages in the prior window
            </BodyText>
            <BodyText style={{ marginBottom: theme.spacing.sm }}>
              • <strong>Mindstate Strength:</strong> Time spent at each mindstate strength level in the prior window
            </BodyText>
            <BodyText style={{ marginBottom: '0' }}>
              • <strong>Emotional Factors:</strong> Average emotional factor levels in the prior window
            </BodyText>
          </div>
        </div>
      </Card>
    </Container>
  )
}

export default PredictorsPage
